import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormikProps } from 'formik';
import { Button } from '@mui/material';
import { useAppDispatch } from '../../store';
import { selectWorkplaces } from '../../store/selectors';
import { addMessage } from '../../store/localApp';
import {
  clearWorkplaceMatches,
  createWorkplace,
  deleteWorkplace,
  emptyWorkplacesFilter,
  fetchDeletionImpact,
  fetchMembershipSummary,
  fetchWorkplaceMatches,
  fetchWorkplaces,
  fetchWorkplaceStats,
  isFilterActive,
  isSameFilter,
  updateWorkplace,
  WorkplaceAssignmentKind,
  WorkplacesFilter,
  deleteWorkplaceAssignment,
  saveWorkplaceAssignment,
  updateWorkplaceAssignmentValidity,
} from '../../store/workplaces';
import { WorkplaceErrorField } from '../../helpers/workplaceErrors';
import { useAppTranslation } from '../../services/i18n';
import { LoadingSpinner } from '../loading/LoadingSpinner';
import {
  ancestorIdsOf,
  buildWorkplaceTree,
  financialApproversOf,
  DropInstruction,
  expandableIdsOf,
  flattenTree,
  forbiddenDropIdsOf,
  leadershipOf,
  resolveDrop,
  visibleIdsFor,
  WorkplaceLeadership,
  WorkplaceNode,
} from '../../helpers/workplaceTree';
import { WorkplaceFilters } from './WorkplaceFilters';
import { WorkplaceTree } from './WorkplaceTree';
import { ROOT_PARENT, WorkplaceDetail, WorkplaceValues } from './WorkplaceDetail';
import { DeleteChoice, WorkplaceDeleteDialog } from './WorkplaceDeleteDialog';
import { WorkplaceMoveDialog } from './WorkplaceMoveDialog';
import { WorkplaceUnsavedDialog } from './WorkplaceUnsavedDialog';
import { apiDateToYmd, todayYmd } from '../../helpers/date';

const SEARCH_DEBOUNCE = 300;

interface EditorProps {
  /**
   * Off means a flat list: one model, another view. Nesting is hidden from the editor
   * rather than merely discouraged -- the backend refuses a parent outright while the
   * company space has the tree switched off.
   */
  isTreeEnabled: boolean;
  /** only then can a member be on other workplaces too, which the Membership tab reports */
  isMultipleWorkplacesEnabled: boolean;
}

export const WorkplacesEditor: React.FC<EditorProps> = ({
  isTreeEnabled,
  isMultipleWorkplacesEnabled,
}) => {
  const t = useAppTranslation();
  const dispatch = useAppDispatch();
  const {
    items,
    isLoading,
    isLoaded,
    loadError,
    matchedIds,
    appliedFilter,
    isFiltering,
    filterError,
    isSaving,
    stats,
  } = useSelector(selectWorkplaces);

  const [filter, setFilter] = useState<WorkplacesFilter>(emptyWorkplacesFilter);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** undefined while nothing is being created, otherwise the parent of the new workplace */
  const [draftParentId, setDraftParentId] = useState<number | null | undefined>(undefined);
  const [nodeToDelete, setNodeToDelete] = useState<WorkplaceNode | null>(null);
  const [nodeToMove, setNodeToMove] = useState<WorkplaceNode | null>(null);
  /** which form field the server refused, if any */
  const [fieldError, setFieldError] = useState<WorkplaceErrorField | undefined>(undefined);
  const [deletionImpact, setDeletionImpact] = useState<
    { organization_units_count: number; affected_users_count: number } | undefined
  >(undefined);
  const rootsExpanded = useRef(false);
  /** the unit currently being dragged, if any */
  const [draggedId, setDraggedId] = useState<number | null>(null);
  /** bumped whenever the keyboard should go back to the tree */
  const [focusToken, setFocusToken] = useState(0);
  const returnFocusToTree = useCallback(() => setFocusToken((current) => current + 1), []);
  /** the open form, so leaving it can be weighed against what it still holds */
  const formRef = useRef<FormikProps<WorkplaceValues> | null>(null);
  /** what is waiting for the answer to the unsaved-changes question, if anything */
  const [pendingExit, setPendingExit] = useState<{ run: () => void } | null>(null);

  useEffect(() => {
    dispatch(fetchWorkplaces());
    dispatch(fetchWorkplaceStats());
  }, [dispatch]);

  const tree = useMemo(() => buildWorkplaceTree(items), [items]);

  // roots start open, everything below stays collapsed -- 800 workplaces would be unreadable.
  // Only once: a refetch must not undo a deliberate "collapse all".
  useEffect(() => {
    if (rootsExpanded.current || !tree.roots.length) {
      return;
    }
    rootsExpanded.current = true;
    setExpandedIds(new Set(tree.roots.map((root) => root.id)));
  }, [tree]);

  const filterActive = isFilterActive(filter);
  // busy from the first keystroke or toggle until the matching rows are actually on screen,
  // which covers the debounce window as well as the request itself
  const isFilterBusy = filterActive && (isFiltering || !isSameFilter(appliedFilter, filter));

  useEffect(() => {
    if (!filterActive) {
      dispatch(clearWorkplaceMatches());
      return;
    }
    const timer = setTimeout(() => {
      dispatch(fetchWorkplaceMatches(filter));
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
  }, [dispatch, filter, filterActive]);

  const rows = useMemo(() => {
    const matched = filterActive && matchedIds ? matchedIds : null;
    return flattenTree(tree, {
      expandedIds,
      visibleIds: matched ? visibleIdsFor(tree, matched) : null,
      matchedIds: matched ? new Set(matched) : null,
    });
  }, [tree, expandedIds, matchedIds, filterActive]);

  const expandableIds = useMemo(() => expandableIdsOf(tree), [tree]);
  const areAllExpanded =
    expandableIds.length > 0 && expandableIds.every((id) => expandedIds.has(id));

  const handleToggle = useCallback((id: number) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setExpandedIds((current) => {
      const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => current.has(id));
      return allExpanded ? new Set<number>() : new Set(expandableIds);
    });
  }, [expandableIds]);

  const isFormOpen = isEditing || draftParentId !== undefined;

  const closeForm = useCallback(() => {
    setIsEditing(false);
    setDraftParentId(undefined);
    setFieldError(undefined);
  }, []);

  /**
   * Everything that would take the panel away from a half-finished form goes through
   * here. What is stored is the bare action, never its guarded wrapper -- answering the
   * question runs it as it is, so it cannot ask again.
   */
  const guard = useCallback(
    (action: () => void) => {
      if (isFormOpen && formRef.current?.dirty) {
        setPendingExit({ run: action });
        return;
      }
      action();
    },
    [isFormOpen]
  );

  /**
   * An open form keeps the workplace it was opened on. Walking the tree then only moves
   * the cursor -- the row under it is drawn as active, the one in the form stays drawn as
   * selected -- and the form is left with Cancel or Escape. Without this a stray click
   * would swap the record under a half-finished edit.
   */
  const handleSelect = useCallback(
    (id: number) => {
      if (isEditing || draftParentId !== undefined) {
        return;
      }
      setSelectedId(id);
      setFieldError(undefined);
    },
    [isEditing, draftParentId]
  );

  /** Escape steps one level up at a time: out of the form, then out of the selection */
  const handleEscape = useCallback(() => {
    if (isFormOpen) {
      guard(closeForm);
      return;
    }
    setSelectedId(null);
  }, [isFormOpen, guard, closeForm]);

  const handleCancel = useCallback(
    () =>
      guard(() => {
        closeForm();
        returnFocusToTree();
      }),
    [guard, closeForm, returnFocusToTree]
  );

  const openEdit = useCallback((id: number) => {
    setSelectedId(id);
    setIsEditing(true);
    setDraftParentId(undefined);
    setFieldError(undefined);
    // editing needs the detail panel, so the split view comes back
    setIsFullscreen(false);
  }, []);

  const handleEdit = useCallback((id: number) => guard(() => openEdit(id)), [guard, openEdit]);

  const startCreating = useCallback((parentId: number | null) => {
    setDraftParentId(parentId);
    setIsEditing(false);
    setFieldError(undefined);
    setIsFullscreen(false);
    if (parentId !== null) {
      setExpandedIds((current) => new Set(current).add(parentId));
    }
  }, []);

  const requestCreate = useCallback(
    (parentId: number | null) => guard(() => startCreating(parentId)),
    [guard, startCreating]
  );

  const selectedNode = selectedId !== null ? tree.byId.get(selectedId) ?? null : null;

  const revealParent = useCallback(
    (parentId: number | null) => {
      setExpandedIds((current) => {
        const next = new Set(current);
        if (parentId !== null) {
          // the whole chain above the target, otherwise the unit lands inside
          // a collapsed branch and looks lost
          ancestorIdsOf(tree, parentId).forEach((id) => next.add(id));
          next.add(parentId);
        }
        return next;
      });
    },
    [tree]
  );

  /** whichever of the two sets of assignments this kind stands for */
  const assignmentsOf = useCallback(
    (node: WorkplaceNode, kind: WorkplaceAssignmentKind) =>
      kind === 'leadership' ? leadershipOf(node.unit) : financialApproversOf(node.unit),
    []
  );

  /**
   * Sends only what the form actually changed. Runs after the workplace itself is
   * saved, because a brand new one has no id to hang an assignment on until then.
   * Returns whether anything was sent, so an untouched card costs no requests and
   * no refetch.
   */
  const applyAssignments = useCallback(
    async (
      unitId: number,
      assignments: WorkplaceValues['assignments'],
      node: WorkplaceNode | null
    ) => {
      let changed = false;
      let isComplete = true;

      /** each slot is its own request; whatever went through stays saved */
      const record = (refused: boolean) => {
        changed = true;
        isComplete = isComplete && !refused;
      };

      for (const kind of ['leadership', 'budget'] as WorkplaceAssignmentKind[]) {
        const wanted = assignments[kind];
        const current: WorkplaceLeadership = node
          ? assignmentsOf(node, kind)
          : { leaders: [] };

        // Leaders are a set, not a slot: whoever left is deleted, whoever arrived is
        // created, and the ones who stayed cost no request at all.
        for (const holder of current.leaders) {
          if (wanted.leaders.includes(holder.user_in_company_space_id)) {
            continue;
          }
          record(
            deleteWorkplaceAssignment.rejected.match(
              await dispatch(deleteWorkplaceAssignment({ unitId, kind, id: holder.id }))
            )
          );
        }
        for (const personId of wanted.leaders) {
          if (current.leaders.some((held) => held.user_in_company_space_id === personId)) {
            continue;
          }
          record(
            saveWorkplaceAssignment.rejected.match(
              await dispatch(
                saveWorkplaceAssignment({
                  unitId,
                  kind,
                  role: 'leader',
                  userInCompanySpaceId: personId,
                  validFrom: todayYmd(),
                  validTo: null,
                })
              )
            )
          );
        }

        // The stand-in is a single slot and the only assignment carrying dates, which
        // can move without the person changing.
        const personId = wanted.deputy;
        const held = current.deputy;
        const isSamePerson = !!personId && !!held && personId === held.user_in_company_space_id;
        const validFrom = wanted.validFrom || todayYmd();
        const validTo = wanted.validTo || null;
        const validityMoved =
          !!held &&
          (validFrom !== apiDateToYmd(held.valid_from) ||
            (validTo || '') !== apiDateToYmd(held.valid_to));

        if (!personId && held) {
          record(
            deleteWorkplaceAssignment.rejected.match(
              await dispatch(deleteWorkplaceAssignment({ unitId, kind, id: held.id }))
            )
          );
        } else if (personId && !isSamePerson) {
          record(
            saveWorkplaceAssignment.rejected.match(
              await dispatch(
                saveWorkplaceAssignment({
                  unitId,
                  kind,
                  role: 'deputy',
                  currentId: held?.id,
                  userInCompanySpaceId: personId,
                  validFrom,
                  validTo,
                })
              )
            )
          );
        } else if (validityMoved && held) {
          record(
            updateWorkplaceAssignmentValidity.rejected.match(
              await dispatch(
                updateWorkplaceAssignmentValidity({ unitId, kind, id: held.id, validFrom, validTo })
              )
            )
          );
        }
      }

      return { changed, isComplete };
    },
    [dispatch, assignmentsOf]
  );

  /** true only when the form is done with -- saved whole and closed */
  const handleSave = useCallback(
    async (values: WorkplaceValues): Promise<boolean> => {
      setFieldError(undefined);
      // the form carries the root as a scalar and a blank code as an empty string;
      // the API wants null for both
      const parentId = values.parentId === ROOT_PARENT ? null : values.parentId;
      const unit = { name: values.name.trim(), code: values.code.trim() || null, parentId };
      if (draftParentId !== undefined) {
        const created = await dispatch(createWorkplace(unit));
        if (createWorkplace.rejected.match(created)) {
          setFieldError(created.payload);
          return false;
        }
        const createdId = (created.payload as { id: number }).id;
        // the assignments can only be hung on the workplace once it has an id, so a
        // refused one leaves the workplace itself created and selected
        const assigned = await applyAssignments(createdId, values.assignments, null);
        setDraftParentId(undefined);
        revealParent(parentId);
        setSelectedId(createdId);
        dispatch(fetchWorkplaces());
        // the workplace itself is created either way, but a refused assignment reopens the
        // form so the offending card can be corrected; the error has already been reported
        // by the API middleware, and a success toast beside it would only contradict it
        if (!assigned.isComplete) {
          setIsEditing(true);
          return false;
        }
        dispatch(addMessage({ severity: 'success', title: t('workplaces.detail.created') }));
        returnFocusToTree();
        return true;
      }
      if (!selectedNode) {
        return false;
      }
      const parentChanged = (selectedNode.parentId ?? null) !== parentId;
      const result = await dispatch(
        updateWorkplace({
          id: selectedNode.id,
          body: { name: unit.name, code: unit.code, parent_id: parentId },
        })
      );
      if (updateWorkplace.rejected.match(result)) {
        setFieldError(result.payload);
        return false;
      }
      const assigned = await applyAssignments(selectedNode.id, values.assignments, selectedNode);
      if (assigned.changed || parentChanged) {
        dispatch(fetchWorkplaces());
      }
      // a refused assignment keeps the form open so the offending card can be corrected;
      // the error itself has already been reported by the API middleware
      if (!assigned.isComplete) {
        return false;
      }
      dispatch(addMessage({ severity: 'success', title: t('workplaces.detail.saved') }));
      setIsEditing(false);
      returnFocusToTree();
      if (parentChanged) {
        revealParent(parentId);
      }
      return true;
    },
    [
      dispatch,
      selectedNode,
      draftParentId,
      revealParent,
      returnFocusToTree,
      applyAssignments,
      t,
    ]
  );

  const keepEditing = useCallback(() => setPendingExit(null), []);

  const navigateAway = useCallback(
    (href: string) =>
      guard(() => {
        window.location.href = href;
      }),
    [guard]
  );

  const loadMembershipSummary = useCallback(
    async (id: number) => {
      const result = await dispatch(fetchMembershipSummary(id));
      return fetchMembershipSummary.fulfilled.match(result) ? result.payload : undefined;
    },
    [dispatch]
  );

  const discardChanges = useCallback(() => {
    const exit = pendingExit;
    setPendingExit(null);
    exit?.run();
  }, [pendingExit]);

  /**
   * Saves from inside the dialog and only then lets go: a refused save keeps the form,
   * its values and the server's complaint, which is what the panel's own Save does too.
   */
  const saveAndExit = useCallback(async () => {
    const values = formRef.current?.values;
    const exit = pendingExit;
    if (!values) {
      return;
    }
    const saved = await handleSave(values);
    setPendingExit(null);
    if (saved) {
      exit?.run();
    }
  }, [pendingExit, handleSave]);

  /** siblings in render order, which is what the reorder shortcut walks through */
  const siblingsOf = useCallback(
    (node: WorkplaceNode): WorkplaceNode[] =>
      node.parentId !== null ? tree.byId.get(node.parentId)?.children ?? [] : tree.roots,
    [tree]
  );

  const moveTo = useCallback(
    async (node: WorkplaceNode, body: { parent_id?: number | null; position?: number }) => {
      const result = await dispatch(updateWorkplace({ id: node.id, body }));
      if (updateWorkplace.rejected.match(result)) {
        // no form is open here, so a refused move has to be reported on its own
        if (result.payload) {
          dispatch(
            addMessage({ severity: 'error', title: t('workplaces.detail.error.' + result.payload) })
          );
        }
        return;
      }
      // both a reparent and a reorder renumber the siblings on the server
      dispatch(fetchWorkplaces());
    },
    [dispatch, t]
  );

  const handleMoveConfirm = useCallback(
    (parentId: number | null) => {
      const node = nodeToMove;
      setNodeToMove(null);
      returnFocusToTree();
      if (!node) {
        return;
      }
      // the new parent is opened, otherwise the unit lands inside a collapsed branch
      if (parentId !== null) {
        setExpandedIds((current) => {
          const next = new Set(current);
          ancestorIdsOf(tree, parentId).forEach((id) => next.add(id));
          next.add(parentId);
          return next;
        });
      }
      moveTo(node, { parent_id: parentId });
    },
    [nodeToMove, tree, moveTo, returnFocusToTree]
  );

  const forbiddenDropIds = useMemo(
    () => (draggedId === null ? null : forbiddenDropIdsOf(tree, draggedId)),
    [tree, draggedId]
  );

  const handleDrop = useCallback(
    (dragged: number, targetId: number, instruction: DropInstruction) => {
      setDraggedId(null);
      const node = tree.byId.get(dragged);
      const move = resolveDrop(tree, dragged, targetId, instruction);
      // a drop that would put the unit back where it already is asks nothing of the server
      if (!node || !move) {
        return;
      }
      if (move.parentId !== null) {
        setExpandedIds((current) => new Set(current).add(move.parentId as number));
      }
      // parent and slot travel in one request, so the move stays a single atomic update
      moveTo(node, { parent_id: move.parentId, position: move.position });
    },
    [tree, moveTo]
  );

  const handleReorder = useCallback(
    (id: number, delta: -1 | 1) => {
      const node = tree.byId.get(id);
      if (!node) {
        return;
      }
      const siblings = siblingsOf(node);
      const index = siblings.findIndex((sibling) => sibling.id === id);
      const target = siblings[index + delta];
      if (!target) {
        return;
      }
      moveTo(node, { position: target.unit.position ?? index + delta + 1 });
    },
    [tree, siblingsOf, moveTo]
  );

  const handleIndent = useCallback(
    (id: number) => {
      const node = tree.byId.get(id);
      if (!node) {
        return;
      }
      const siblings = siblingsOf(node);
      const index = siblings.findIndex((sibling) => sibling.id === id);
      const newParent = siblings[index - 1];
      // the first child has nothing to nest under
      if (!newParent) {
        return;
      }
      setExpandedIds((current) => new Set(current).add(newParent.id));
      moveTo(node, { parent_id: newParent.id });
    },
    [tree, siblingsOf, moveTo]
  );

  const handleOutdent = useCallback(
    (id: number) => {
      const node = tree.byId.get(id);
      if (!node || node.parentId === null) {
        return;
      }
      const parent = tree.byId.get(node.parentId);
      moveTo(node, { parent_id: parent?.parentId ?? null });
    },
    [tree, moveTo]
  );

  const handleDeleteRequest = useCallback(
    async (id: number) => {
      const node = tree.byId.get(id);
      if (!node) {
        return;
      }
      setNodeToDelete(node);
      setDeletionImpact(undefined);
      const result = await dispatch(fetchDeletionImpact(id));
      if (!fetchDeletionImpact.rejected.match(result)) {
        setDeletionImpact(result.payload);
      }
    },
    [dispatch, tree]
  );

  const handleDeleteConfirm = useCallback(
    async (choice: DeleteChoice) => {
      if (!nodeToDelete) {
        return;
      }
      const result = await dispatch(deleteWorkplace({ id: nodeToDelete.id, ...choice }));
      if (deleteWorkplace.rejected.match(result)) {
        return;
      }
      dispatch(addMessage({ severity: 'success', title: t('workplaces.detail.deleted') }));
      setNodeToDelete(null);
      setSelectedId(null);
      setIsEditing(false);
      dispatch(fetchWorkplaces());
      returnFocusToTree();
    },
    [dispatch, nodeToDelete, returnFocusToTree, t]
  );

  if (isLoading && !isLoaded) {
    return <LoadingSpinner />;
  }

  if (loadError) {
    return (
      <div className={'workplace-editor__state'}>
        <p>{t('workplaces.loadError')}</p>
        <Button variant={'contained'} onClick={() => dispatch(fetchWorkplaces())}>
          {t('workplaces.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className={'workplace-editor'}>
      <WorkplaceFilters
        filter={filter}
        isFiltering={isFilterBusy}
        onChange={setFilter}
        onReset={() => setFilter(emptyWorkplacesFilter)}
      />

      {/* the counts describe the whole organization, so the filter does not touch them */}
      <p className={'workplace-editor__stats'}>
        {[
          t('workplaces.stats.workplaces', { count: items.length }),
          stats.users !== undefined && t('workplaces.stats.users', { count: stats.users }),
          stats.withoutWorkplace !== undefined &&
            t('workplaces.stats.withoutWorkplace', { count: stats.withoutWorkplace }),
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>

      <div
        className={
          'workplace-editor__split' + (isFullscreen ? ' workplace-editor__split--fullscreen' : '')
        }
      >
        <WorkplaceTree
          rows={rows}
          selectedId={selectedId}
          isFilterActive={filterActive}
          isFiltering={isFilterBusy}
          filterError={!!filterError}
          isFullscreen={isFullscreen}
          areAllExpanded={areAllExpanded}
          canToggleAll={isTreeEnabled && expandableIds.length > 0}
          canNest={isTreeEnabled && expandableIds.length > 0}
          forbiddenDropIds={forbiddenDropIds}
          onDragStart={setDraggedId}
          onDragEnd={() => setDraggedId(null)}
          onDrop={handleDrop}
          onToggle={handleToggle}
          onToggleAll={handleToggleAll}
          onSelect={handleSelect}
          onEdit={handleEdit}
          onToggleFullscreen={() => setIsFullscreen((current) => !current)}
          onCreateRoot={() => requestCreate(null)}
          onCreateSibling={(id) =>
            // flat mode has no parent field to show it in and the API refuses a parent
            // anyway, so a leftover nested row must not pass its own on
            requestCreate(isTreeEnabled ? (tree.byId.get(id)?.parentId ?? null) : null)
          }
          onCreateChild={(id) => requestCreate(id)}
          onReorder={handleReorder}
          isTreeEnabled={isTreeEnabled}
          isEmpty={isLoaded && items.length === 0}
          onMoveTo={(id) =>
            guard(() => {
              closeForm();
              setNodeToMove(tree.byId.get(id) ?? null);
            })
          }
          onIndent={handleIndent}
          onOutdent={handleOutdent}
          onDelete={(id) =>
            guard(() => {
              closeForm();
              handleDeleteRequest(id);
            })
          }
          onDeselect={handleEscape}
          focusToken={focusToken}
        />
        {!isFullscreen && (
          <WorkplaceDetail
            tree={tree}
            node={selectedNode}
            draftParentId={draftParentId}
            isTreeEnabled={isTreeEnabled}
            isMultipleWorkplacesEnabled={isMultipleWorkplacesEnabled}
            isCreating={draftParentId !== undefined}
            isEditing={isEditing}
            isSaving={isSaving}
            fieldError={fieldError}
            innerRef={formRef}
            onEdit={() => setIsEditing(true)}
            onCancel={handleCancel}
            onEscape={handleEscape}
            onSave={handleSave}
            onLoadMembershipSummary={loadMembershipSummary}
            onNavigate={navigateAway}
          />
        )}
      </div>

      {nodeToDelete && (
        <WorkplaceDeleteDialog
          tree={tree}
          node={nodeToDelete}
          impact={deletionImpact}
          isSaving={isSaving}
          onCancel={() => {
            setNodeToDelete(null);
            returnFocusToTree();
          }}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {nodeToMove && (
        <WorkplaceMoveDialog
          tree={tree}
          node={nodeToMove}
          isSaving={isSaving}
          onCancel={() => {
            setNodeToMove(null);
            returnFocusToTree();
          }}
          onConfirm={handleMoveConfirm}
        />
      )}

      {pendingExit && (
        <WorkplaceUnsavedDialog
          name={
            draftParentId !== undefined
              ? formRef.current?.values.name.trim() || t('workplaces.unsaved.newWorkplace')
              : selectedNode?.unit.name ?? ''
          }
          canSave={!!formRef.current?.isValid}
          isSaving={isSaving}
          onDiscard={discardChanges}
          onKeepEditing={keepEditing}
          onSave={saveAndExit}
        />
      )}
    </div>
  );
};
